import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { SettingsNav, SettingsNavGroup, SettingsNavItem, SettingsWrapper } from '@/new/components/settings-nav';
import { cn } from '@/new/lib/utils';
import { ActivityDashboard } from '@/new/pages/components/activity-dashboard';
import { AuditTab } from '@/new/pages/components/audit-tab';
import { BillingUsage } from '@/new/pages/components/billing-usage';

const USAGE_VIEW_PERMISSIONS = [Permission.UsageDashboardView, Permission.PlatformViewUsage];

interface UsageSection {
  id: string;
  title: string;
  icon: string;
  permissions: Permission[];
  render: (organisationId: string) => React.ReactNode;
}

const sections: UsageSection[] = [
  {
    id: 'activity',
    title: 'Activity',
    icon: 'lucide--chart-no-axes-combined',
    permissions: USAGE_VIEW_PERMISSIONS,
    render: (organisationId) => <ActivityDashboard organisationId={organisationId} />,
  },
  {
    id: 'usage',
    title: 'Credit usage',
    icon: 'lucide--chart-column',
    permissions: USAGE_VIEW_PERMISSIONS,
    render: (organisationId) => <BillingUsage organisationId={organisationId} />,
  },
  {
    id: 'audit',
    title: 'Audit',
    icon: 'lucide--clock',
    permissions: [Permission.AuditView],
    render: (organisationId) => <AuditTab organisationId={organisationId} isViewingOtherOrg={false} settingsLayout />,
  },
];

export function Usage() {
  const location = useLocation();
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const activeSection = sections.find((section) => location.pathname.includes(`/usage/${section.id}`))?.id;
  const allowedSections = sections.filter((section) => section.permissions.some(hasPermission));

  if (!user) return null;
  if (allowedSections.length === 0) return <Navigate to="/matters" replace />;

  // Sections a role can't see fall back to its first allowed one, e.g. audit-only roles land on Audit.
  const defaultPath = `/usage/${allowedSections[0].id}`;

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={[{ href: '/usage', label: 'Usage', active: true }]} />
      <SettingsWrapper>
        <SettingsNav className="max-w-56">
          <SettingsNavGroup>
            {allowedSections.map((section) => (
              <SettingsNavItem key={section.id} asChild active={activeSection === section.id}>
                <Link to={`/usage/${section.id}`}>
                  <Icon className={section.icon} />
                  <span>{section.title}</span>
                </Link>
              </SettingsNavItem>
            ))}
          </SettingsNavGroup>
          <SettingsNavGroup className="mt-auto">
            <Link
              to="/legacy-usage"
              className="mb-1 ml-1 flex items-start gap-1.5 text-[13px] text-gray-500 duration-150 hover:text-gray-900"
            >
              <Icon className="lucide--chevron-left mt-0.75" />
              <span>Return to legacy usage dashboard</span>
            </Link>
          </SettingsNavGroup>
        </SettingsNav>
        <div
          className={cn(
            'min-h-0 min-w-0 flex-1',
            activeSection === 'audit' ? 'flex flex-col overflow-hidden pt-7' : 'overflow-auto px-8 pt-7 pb-8',
          )}
        >
          <Routes>
            <Route index element={<Navigate to={defaultPath} replace />} />
            {sections.map((section) => (
              <Route
                key={section.id}
                path={section.id}
                element={
                  allowedSections.includes(section) ? (
                    section.render(user.organisationId)
                  ) : (
                    <Navigate to={defaultPath} replace />
                  )
                }
              />
            ))}
            {/* Older section URLs. */}
            <Route path="stats" element={<Navigate to="/usage/activity" replace />} />
            <Route path="billing" element={<Navigate to="/usage/usage" replace />} />
            <Route path="credit" element={<Navigate to="/usage/usage" replace />} />
          </Routes>
        </div>
      </SettingsWrapper>
    </div>
  );
}

/** Keeps saved `/new-usage/<section>` links working now that the dashboard lives at `/usage`. */
export function NewUsageRedirect() {
  const { '*': section = '' } = useParams();
  return <Navigate to={`/usage/${section}`} replace />;
}
